import { useEffect, useMemo, useRef } from 'react';
import tw, { TwStyle } from 'twin.macro';

import { ToolTip } from '@insights_module/components/ToolTip';
import { Loader } from '@components/Loader';
import { useDebouncedFunction } from '@app/hooks/useDebounce';
import { ArrowUp } from '@icons/ArrowUp';
import { ArrowDown } from '@icons/ArrowDown';
import { PAGE_FETCHING_OFFSET, SORT_ORDER } from '@insights_module/constants/defaults';

const TableContainer = tw.div`flex flex-col w-full`;
const THead = tw.div`border-t border-gray-1 items-center`;
const THR = tw.div`flex flex-row bg-neutral-background pr-6 text-left text-[11px] text-text-and-icon-muted-2 font-semibold select-none`;
const RowsWrapper = tw.div`absolute top-0 left-0 bottom-0 right-0 overflow-auto border-t border-gray-1`;
const TR = tw(
  THR
)`bg-page-background text-[13px] text-text-and-icon font-medium h-[78px] border-b border-gray-1`;
const TD = tw.div`flex items-center relative`;
const LoadingOverlay = tw.div`absolute top-10 left-0 right-0 bottom-0 z-50 bg-page-background flex items-center justify-center`;
const ClickableHeadCell = tw.div`items-center justify-start cursor-pointer hover:underline rounded whitespace-nowrap flex gap-[2px]`;
const TooltipContent = tw.div`text-[11px] w-auto max-w-[180px]`;

export type ColumnDefinitionType<T, K extends keyof T> = {
  key: K;
  header: string | JSX.Element;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  columnRenderer?: (item: T[K]) => JSX.Element;
};

type TableHeaderProps<T, K extends keyof T> = {
  columns: Array<ColumnDefinitionType<T, K>>;
  customStyles?: { headStyles?: TwStyle; headRowStyles?: TwStyle };
};

type TableRowsProps<T, K extends keyof T> = {
  data: Array<T>;
  columns: Array<ColumnDefinitionType<T, K>>;
  tablePrefix: string;
  onReachEnd?: () => void;
  isLoadingMore?: boolean;
  customStyles?: { bodyStyles?: TwStyle; bodyRowStyles?: TwStyle; selectedRowStyles?: TwStyle };
};

export type TableStyles = {
  tableStyles?: TwStyle;
  headStyles?: TwStyle;
  headRowStyles?: TwStyle;
  bodyStyles?: TwStyle;
  bodyRowStyles?: TwStyle;
  selectedRowStyles?: TwStyle;
};

export type TableProps<T, K extends keyof T> = {
  data: Array<T>;
  columns: Array<ColumnDefinitionType<T, K>>;
  tablePrefix?: string;
  customStyles?: TableStyles;
  tableBanner?: JSX.Element;
  onReachEnd?: () => void;
  isLoading: boolean;
  isLoadingMore?: boolean;
  noDataFallback?: JSX.Element;
  errorFallback?: JSX.Element | null;
};

const LoadingRow = ({ isLoading }: { isLoading: boolean | undefined }): JSX.Element => (
  <TR
    css={[
      tw`transition-all duration-200`,
      isLoading ? tw`h-10 flex items-center justify-center` : tw`h-0 hidden`,
    ]}
    data-testid='load-more-row'>
    <Loader size='sm' theme='dark' />
  </TR>
);

const Table = <T, K extends keyof T>({
  data,
  columns,
  tablePrefix = '',
  customStyles = {},
  tableBanner,
  onReachEnd,
  isLoading,
  isLoadingMore,
  noDataFallback,
  errorFallback = null,
}: TableProps<T & { isSelected?: boolean }, K>): JSX.Element => {
  const { tableStyles, headStyles, bodyStyles, headRowStyles, bodyRowStyles, selectedRowStyles } =
    customStyles;

  const fallbackElement = useMemo(() => {
    if (errorFallback) {
      return errorFallback;
    }

    return !data?.length && noDataFallback;
  }, [data, noDataFallback, errorFallback]);

  return (
    <TableContainer css={[tableStyles]} role='table' data-testid='table'>
      <TableHeader columns={columns} customStyles={{ headStyles, headRowStyles }} />
      {tableBanner}

      {isLoading && (
        <LoadingOverlay data-testid='table-loader'>
          <Loader theme='dark' size='lg' />
        </LoadingOverlay>
      )}

      {fallbackElement ? (
        fallbackElement
      ) : (
        <TableRows
          data={data}
          columns={columns}
          tablePrefix={tablePrefix}
          customStyles={{ bodyStyles, bodyRowStyles, selectedRowStyles }}
          onReachEnd={onReachEnd}
          isLoadingMore={isLoadingMore}
        />
      )}
    </TableContainer>
  );
};

const TableHeader = <T, K extends keyof T>({
  columns,
  customStyles = {},
}: TableHeaderProps<T, K>): JSX.Element => {
  const { headStyles, headRowStyles } = customStyles;
  const headers = columns.map((column, index) => {
    return (
      <div
        css={{ minWidth: column.width, textAlign: column.align }}
        key={`header-cell-${index}`}
        data-testid={`header-cell-${index}`}>
        {column.header}
      </div>
    );
  });

  return (
    <THead css={[headStyles]} data-testid='table-head'>
      <THR tw='py-3' css={[headRowStyles]}>
        {headers}
      </THR>
    </THead>
  );
};

const TableRows = <T, K extends keyof T>({
  data = [],
  columns,
  tablePrefix,
  customStyles = {},
  onReachEnd,
  isLoadingMore,
}: TableRowsProps<T, K>): JSX.Element => {
  const { bodyStyles, bodyRowStyles, selectedRowStyles } = customStyles;
  const rowsWrapperRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const debouncedFunction = useDebouncedFunction(
    onReachEnd ? (onReachEnd as () => void) : () => null,
    100
  );

  // if tablePrefix is not provided, we don't want to add a dash to the id
  tablePrefix = tablePrefix ? `${tablePrefix}-` : '';

  useEffect(() => {
    if (!onReachEnd) {
      return;
    }

    const fetchMoreCallBack = () => {
      const position = loadMoreTriggerRef.current?.getBoundingClientRect();

      if (position && position?.top < window.innerHeight && position?.bottom >= 0) {
        !isLoadingMore && debouncedFunction();
      }
    };

    rowsWrapperRef?.current?.addEventListener('scroll', fetchMoreCallBack);

    return () => {
      if (rowsWrapperRef.current) {
        return rowsWrapperRef?.current.removeEventListener('scroll', fetchMoreCallBack);
      }
    };
  }, [rowsWrapperRef, loadMoreTriggerRef.current, isLoadingMore]);

  const rows = data?.map((row: any, rowIndex) => {
    if (row?.garm && row?.garm.isLoading !== undefined && row?.garm.isLoading) {
      return <TR key={`${tablePrefix}row-${rowIndex}`} tw='h-0 hidden' />;
    }

    return (
      <TR
        key={`${tablePrefix}row-${rowIndex}`}
        css={[bodyRowStyles, row.isSelected && selectedRowStyles]}
        id={`${tablePrefix}row-${rowIndex}`}
        data-testid={`${tablePrefix}row-${rowIndex}`}
        role='row'>
        {columns.map((column, colIndex) => {
          return (
            <TD
              key={`${tablePrefix}cell-${colIndex}`}
              css={{
                minWidth: column.width,
                textAlign: column.align,
                justifyContent: column.align,
                // minWidth: colIndex === 0 ? column.width : 0,
              }}
              role='cell'>
              {column.columnRenderer
                ? column.columnRenderer(row[column.key])
                : String(row[column.key])}
            </TD>
          );
        })}
      </TR>
    );
  });

  // Specify the index of the row that will trigger data fetching.
  const loadMoreElementIndex = useMemo(
    () =>
      Math.sign(data.length - PAGE_FETCHING_OFFSET) > 0 ? data.length - PAGE_FETCHING_OFFSET : 0,
    [data, loadMoreTriggerRef.current]
  );

  // Insert loadMoreTriggerElement to the rows.
  rows.splice(
    loadMoreElementIndex,
    0,
    <div
      ref={loadMoreTriggerRef}
      tw='h-0'
      key='trigger-fetch-key'
      data-testid='on-reach-end-trigger'
    />
  );

  return (
    <RowsWrapper tw='top-10' css={[bodyStyles]} ref={rowsWrapperRef} data-testid='table-body'>
      {rows}
      <LoadingRow isLoading={isLoadingMore || false} />
    </RowsWrapper>
  );
};

export const SortableHead = ({
  isVisible,
  order,
  tooltip,
  title,
  onClick,
  ...props
}: {
  isVisible: boolean;
  order: string;
  tooltip: string;
  title: string;
  onClick: () => void;
}): JSX.Element | null => (
  <ToolTip placement='top' content={<TooltipContent>{tooltip}</TooltipContent>}>
    <ClickableHeadCell onClick={onClick} {...props} title={title}>
      <div tw='truncate'>{title}</div>
      {isVisible && (
        <div>
          {order === SORT_ORDER.DESC ? (
            <ArrowDown tw='fill-text-and-icon-muted-2 inline-block -mt-0.5 ml-1' />
          ) : (
            <ArrowUp tw='fill-text-and-icon-muted-2 inline-block -mt-0.5 ml-1' />
          )}
        </div>
      )}
    </ClickableHeadCell>
  </ToolTip>
);

export { Table };
